import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Injector,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CONFIG_CONTEXT } from '../../../../enum/config-context.enum';
import { BaseTunnelObject } from '../../model/base-tunnel-object';
import {
  faArrowRight,
  faChevronDown,
  faChevronUp,
  faList,
  faMinus,
  faPlus,
  faShoppingCart,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { ComplementaryProduct } from '../../../../models/configurators-2d/complementary-products/complementary-product';
import { KitchenService } from '../../../../services/configurators-2d/applications/kitchen.service';
import { TunnelDataStore } from '../../../../stores/tunnel/tunnel-data.store';
import { TunnelNavigationStore } from '../../../../stores/tunnel/tunnel-navigation.store';
import { MODAL, ModalService } from '../../../../services/modal.service';
import { Price } from '../../../../stores/tunnel/model/price';
import { DiscountTypeEnum } from '../../../../enum/discount-type.enum';
import { ComplementaryProductOrderService } from 'src/app/services/complementary-products/complementary-product-order.service';
import { KitchenConfigurationData } from '../../../../stores/tunnel/model/configuration-data/kitchen-configuration-data';
import { ComplementaryProductFavoriteService } from 'src/app/services/complementary-products/complementary-product-favorite.service';
import {
  auditTime,
  BehaviorSubject,
  combineLatest,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  from,
  groupBy,
  map,
  mergeMap,
  shareReplay,
  startWith,
  Subject,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QueueImageLoaderService } from 'src/app/services/queue-image-loader.service';
import { MenuItem, TreeNode } from 'primeng/api';
import { animate, style, transition, trigger } from '@angular/animations';
import { ALL_PRODUCT_TREE_NODE } from 'src/app/pages/tunnel/common/base-complementary-product/complementary-product-filter/complementary-product-filter.component';

export class DisplayedProduct extends ComplementaryProduct {
  loading: boolean = false;
  imageLoaded: boolean = false;
  inCart?: boolean;
}

export class DisplayedComplementaryProductCategory {
  category: string;
  subCategories: string[];
  enabled: boolean;
  enabledSubCategories: Set<string> = new Set();
}

export enum ComplementaryProductTab {
  Favorite = 'Mes favoris',
  AllProducts = 'Tous les produits',
}

@Component({
  selector: 'ng-casto-base-complementary-product',
  templateUrl: './base-complementary-product.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ transform: 'translateX(350px)' }),
        animate('150ms ease-out', style({ transform: 'translateX(0)' })),
      ]),
      transition(':leave', [animate('150ms ease-out', style({ transform: 'translateX(350px)' }))]),
    ]),
  ],
})
export class BaseComplementaryProductComponent extends BaseTunnelObject {
  //Inject
  protected kitchenService = inject(KitchenService);
  protected tunnelDataStore = inject(TunnelDataStore);
  protected tunnelNavigationStore = inject(TunnelNavigationStore);
  protected complementaryProductOrderService = inject(ComplementaryProductOrderService);
  protected modalService = inject(ModalService);
  protected injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly imageLoaderService = inject(QueueImageLoaderService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly complementaryProductFavoriteService = inject(ComplementaryProductFavoriteService);

  //Observables
  protected currentContext$ = this.tunnelNavigationStore.getCurrentContext();
  protected currentDiscount$ = this.tunnelDataStore.getCurrentDiscount();
  protected configurationData$ = this.tunnelDataStore.getConfigurationData();
  protected currentComplementaryProduct$ = this.tunnelDataStore.getCurrentComplementaryProduct();
  protected currentPricing$ = this.tunnelDataStore.getCurrentPricing();
  protected pricingLoading$ = this.tunnelDataStore.getPricingLoading();
  protected products$ = new BehaviorSubject<DisplayedProduct[]>([]);
  protected searchInput$ = new BehaviorSubject<string>('');
  protected fetchProductsLoading$ = new BehaviorSubject<boolean>(false);
  protected updateProductLoading$ = new BehaviorSubject<boolean>(false);
  private readonly quantityUpdate$ = new Subject<{ ean: string; quantity: number }>();

  //Observables dérivés
  private readonly search$ = this.searchInput$.pipe(
    map(s => (s ?? '').trim()),
    distinctUntilChanged(),
    debounceTime(200)
  );

  protected discountNumberValue$ = this.currentDiscount$.pipe(
    map(discount => {
      switch (discount) {
        case DiscountTypeEnum.Discount0:
          return 0;
        case DiscountTypeEnum.Discount5:
          return 5;
        case DiscountTypeEnum.Discount10:
          return 10;
        case DiscountTypeEnum.Discount20:
          return 20;
        default:
          return 0;
      }
    }),
    shareReplay(1)
  );

  protected complementaryProductTotal$ = this.currentPricing$.pipe(
    map(pricing => pricing?.totalComplementaryProduct ?? new Price({ baseAmount: 0, actualAmount: 0 }))
  );

  protected displayDiscount$ = combineLatest([this.discountNumberValue$, this.complementaryProductTotal$]).pipe(
    map(([discount, total]) => discount > 0 && total.actualAmount > 0)
  );

  protected displayedProducts$ = combineLatest([
    this.products$,
    this.currentComplementaryProduct$,
    this.complementaryProductFavoriteService.getFavoriteProducts$(),
  ]).pipe(
    map(([products, complementaryProduct, favoriteProducts]) =>
      products.map(product => ({
        ...product,
        inCart: complementaryProduct?.products?.some(p => p.ean === product.ean) ?? false,
        imageLoaded: false,
        inFavorite: favoriteProducts.some(p => p.ean === product.ean),
      }))
    ),
    shareReplay(1)
  );

  protected selectedFilters$ = new BehaviorSubject<TreeNode[]>([ALL_PRODUCT_TREE_NODE]);

  protected filteredProducts$ = combineLatest([this.displayedProducts$, this.search$, this.selectedFilters$]).pipe(
    map(([products, searchInput, selectedFilters]) => this.applyFilters(products, searchInput, selectedFilters)),
    shareReplay(1)
  );

  protected favoriteProducts$ = this.displayedProducts$.pipe(map(products => products.filter(p => p.inFavorite)));

  protected currentKitchenConfigurationId$ = combineLatest([this.currentContext$, this.configurationData$]).pipe(
    map(([context, configData]) => {
      switch (context) {
        case CONFIG_CONTEXT.KITCHEN:
          return (configData?.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData)?.kitchenConfigurationId;
        default:
          return undefined;
      }
    }),
    shareReplay(1)
  );

  protected currentTunnelConfigurationId$ = combineLatest([this.currentContext$, this.configurationData$]).pipe(
    map(([context, configData]) => {
      switch (context) {
        case CONFIG_CONTEXT.KITCHEN:
          return (configData?.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData)?.configurationId;
        default:
          return undefined;
      }
    }),
    shareReplay(1)
  );

  protected complementaryProductViewModel$ = combineLatest({
    displayedProducts: this.displayedProducts$,
    currentContext: this.currentContext$,
    currentDiscount: this.currentDiscount$,
    currentComplementaryProduct: this.currentComplementaryProduct$,
    currentPricing: this.currentPricing$,
    pricingLoading: this.pricingLoading$,
    discountNumberValue: this.discountNumberValue$,
    displayDiscount: this.displayDiscount$,
    complementaryProductTotal: this.complementaryProductTotal$,
    fetchProductsLoading: this.fetchProductsLoading$,
    updateProductLoading: this.updateProductLoading$,
    searchInput: this.searchInput$,
    selectedFilters: this.selectedFilters$,
    hasRemovedProducts: this.currentComplementaryProduct$.pipe(map(cp => cp?.hasRemovedProducts ?? false)),
  });

  //Consts
  protected readonly faShoppingCart = faShoppingCart;
  protected readonly faMinus = faMinus;
  protected readonly faPlus = faPlus;
  protected readonly faTrash = faTrash;
  protected readonly faChevronUp = faChevronUp;
  protected readonly faChevronDown = faChevronDown;
  protected readonly tabMenuItems: MenuItem[] = [
    {
      id: ComplementaryProductTab.Favorite,
      label: ComplementaryProductTab.Favorite,
      icon: 'pi pi-heart-fill',
    },
    {
      id: ComplementaryProductTab.AllProducts,
      label: ComplementaryProductTab.AllProducts,
      icon: 'pi pi-objects-column',
    },
  ];
  protected readonly placeholderImageSrc: string = 'assets/img/models/ash_gris.jpg';

  //Attributes
  private _activeItem: MenuItem = this.tabMenuItems[1]; //par défaut : tout les produits
  protected savedFilterSelection: TreeNode[] = [];
  protected showMarketingCard: boolean = true;
  protected isFilterMenuOpen: boolean = false;

  protected set activeItem(value: MenuItem) {
    this._activeItem = value;
  }

  protected get activeItem(): MenuItem {
    return this._activeItem;
  }

  public override ngOnInit() {
    super.ngOnInit();
    this.subscribeToObservable();
    this.quantityUpdate$
      .pipe(
        groupBy(x => x.ean),
        mergeMap(group$ =>
          group$.pipe(
            auditTime(300),
            concatMap(({ ean, quantity }) => this.persistQuantity(ean, quantity))
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  @ViewChild('filterInput')
  set filterInput(element: ElementRef<HTMLInputElement>) {
    if (element?.nativeElement) {
      element.nativeElement.focus();
    }
  }

  private async initProductsFromContext(context: CONFIG_CONTEXT): Promise<void> {
    try {
      this.fetchProductsLoading$.next(true);
      switch (context) {
        case CONFIG_CONTEXT.KITCHEN:
          const kitchenProducts = await this.kitchenService.getKitchenComplementaryProductList();
          await this.complementaryProductFavoriteService.loadUserFavorites();
          const displayedProducts = kitchenProducts.map(p => ({
            ...p,
            loading: false,
            labelLower: (p.label ?? '').toLowerCase(),
          })) as Array<DisplayedProduct & { labelLower: string }>;
          this.products$.next(displayedProducts);

          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Erreur lors du chargement des produits complémentaires:', error);
    } finally {
      this.fetchProductsLoading$.next(false);
    }
  }

  private syncProductQuantities(): void {
    const orderedProducts = this.tunnelDataStore.getCurrentStateValue().currentComplementaryProduct?.products;
    if (!orderedProducts) return;

    const currentProducts = this.products$.getValue();
    const updatedProducts = currentProducts.map(product => {
      const existingProduct = orderedProducts.find(p => p.ean === product.ean);
      if (existingProduct) {
        return { ...product, quantity: existingProduct.quantity };
      } else {
        return { ...product, quantity: 0 };
      }
    });

    this.products$.next(updatedProducts);
  }

  protected subscribeToObservable() {
    // Context - charge les produits quand le contexte change
    this.currentContext$.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(async context => {
      if (context) {
        await this.initProductsFromContext(context);
        this.syncProductQuantities();
      }
    });

    // ComplementaryProduct - synchronise les quantités
    this.currentComplementaryProduct$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(complementaryProduct => {
      if (complementaryProduct) {
        this.syncProductQuantities();
      }
    });
  }

  private persistQuantity(ean: string, quantity: number) {
    const product = this.products$.getValue().find(p => p.ean === ean);
    if (!product) return EMPTY;
    return from(this.updateOrderedProduct({ ...product, quantity }));
  }

  protected setProductQuantity(product: DisplayedProduct, quantity: number | string): void {
    if (!quantity) quantity = 0;
    if (typeof quantity === 'string') quantity = Number.parseInt(quantity, 10);
    const current = this.products$.getValue();
    const updated = current.map(p => (p.ean === product.ean ? { ...p, quantity } : p));
    this.products$.next(updated);
    this.quantityUpdate$.next({ ean: product.ean, quantity: Number(quantity) });
  }

  protected updateSearchInput(value: string): void {
    this.searchInput$.next(value);
  }

  private applyFilters(
    products: DisplayedProduct[],
    searchInput?: string,
    selectedFilters?: TreeNode[]
  ): DisplayedProduct[] {
    let filteredProducts = products;

    if (searchInput?.trim()) {
      filteredProducts = this.filterBySearchInput(filteredProducts, searchInput.trim());
    }

    if (selectedFilters?.length) {
      filteredProducts = this.filterByProductCategories(filteredProducts, selectedFilters);
    }

    return filteredProducts;
  }

  private filterBySearchInput(products: DisplayedProduct[], searchInput: string): DisplayedProduct[] {
    // EAN-8, EAN-13 ou EAN-14
    const isEAN = /^\d{8}$|^\d{13}$|^\d{14}$/.test(searchInput);

    if (isEAN) {
      return products.filter(p => p.ean === searchInput);
    }

    const searchLower = searchInput.toLowerCase();
    return products.filter(p => p.label.toLowerCase().includes(searchLower));
  }

  // Méthode pour recevoir les changements de filtres
  protected onFilterChange(filters: TreeNode[]): void {
    this.savedFilterSelection = filters; // Sauvegarde
    this.selectedFilters$.next(filters);
  }

  private filterByProductCategories(products: DisplayedProduct[], selectedFilters: TreeNode[]): DisplayedProduct[] {
    if (selectedFilters.some(f => f.key === 'all')) return products;

    // Catégories sélectionnées
    const selectedCategories = new Set(selectedFilters.filter(f => f.data?.type === 'category').map(f => f.label));

    // Sous-catégories sélectionnées
    const selectedSubCategories = new Set(
      selectedFilters.filter(f => f.data?.type === 'subcategory').map(f => f.label)
    );

    return products.filter(p => {
      const categoryName = p.category?.categoryName;
      const subCategoryName = p.subCategory?.subCategoryName;

      // Si la catégorie seule est sélectionnée (sans sous-catégories)
      if (selectedCategories.has(categoryName)) {
        return true;
      }
      // Si une sous-catégorie est sélectionnée
      if (subCategoryName && selectedSubCategories.has(subCategoryName)) {
        return true;
      }

      return false;
    });
  }

  private async updateOrderedProduct(product: DisplayedProduct): Promise<void> {
    const state = this.tunnelDataStore.getCurrentStateValue();
    const kitchenConfig = state.configurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData;

    try {
      this.updateProductLoading$.next(true);

      // Met à jour le loading du produit spécifique
      const currentProducts = this.products$.getValue();
      const updatedProducts = currentProducts.map(p => (p.ean === product.ean ? { ...p, loading: true } : p));
      this.products$.next(updatedProducts);

      await this.imageLoaderService.prioritizeNetwork(async () => {
        await this.complementaryProductOrderService.upsertComplementaryProductOrder(
          kitchenConfig?.kitchenConfigurationId,
          product
        );
        await this.updateTunnelComplementaryProduct();
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du produit:', error);
    } finally {
      this.updateProductLoading$.next(false);

      // Retire le loading du produit spécifique
      const currentProducts = this.products$.getValue();
      const updatedProducts = currentProducts.map(p => (p.ean === product.ean ? { ...p, loading: false } : p));
      this.products$.next(updatedProducts);
    }
  }

  private async updateTunnelComplementaryProduct(): Promise<void> {
    const state = this.tunnelDataStore.getCurrentStateValue();
    const context = state.mainContext;
    const kitchenConfig = state.configurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData;

    switch (context) {
      case CONFIG_CONTEXT.KITCHEN:
        await this.kitchenService.computeComplementaryProduct({
          tunnelConfigurationId: kitchenConfig?.configurationId,
        });
        break;
    }
  }

  protected openFilter() {
    this.showMarketingCard = false;
    this.isFilterMenuOpen = true;
  }

  protected onFilterAnimationDone(event: any) {
    // Quand l'animation de leave est terminée
    if (event.toState === 'void' && !this.isFilterMenuOpen) {
      this.showMarketingCard = true;
      this.cdr.detectChanges();
    }
  }

  protected requestComplementaryProduct() {
    this.modalService.display(MODAL.REQUEST_COMPLEMENTARY_PRODUCT);
  }

  protected readonly ComplementaryProductTab = ComplementaryProductTab;
  protected readonly faList = faList;
  protected readonly faArrowRight = faArrowRight;
}
